import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { components } from '@trackercop/contracts';

// The /me response as returned by the Workers API. Kept as its own alias
// so call sites don't spell out the full generated path.
export type MeResponse = components['schemas']['MeResponse'];
export type Permission = components['schemas']['Permission'];

interface IdentityContextValue {
  me: MeResponse;
  permissions: Set<Permission>;
  hasPermission: (permission: Permission) => boolean;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

interface IdentityProviderProps {
  value: MeResponse;
  children: ReactNode;
}

// Wraps the /me response in a permission-aware context. Components call
// `useCurrentIdentity()` to read the current user, tenant, and the set of
// permissions their membership role confers. The server is still the
// authority — `hasPermission` is UX only; every mutation endpoint in
// @trackercop/workers re-checks the permission via `authorize()`
// middleware before touching a row.
export function IdentityProvider({ value, children }: IdentityProviderProps) {
  const contextValue = useMemo<IdentityContextValue>(() => {
    const permissions = new Set<Permission>(value.permissions);
    return {
      me: value,
      permissions,
      hasPermission: (permission) => permissions.has(permission),
    };
  }, [value]);
  return <IdentityContext.Provider value={contextValue}>{children}</IdentityContext.Provider>;
}

export function useCurrentIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (!ctx) {
    throw new Error('useCurrentIdentity must be used within <IdentityProvider>');
  }
  return ctx;
}
